import {type ComponentProps, useMemo, useRef, useState} from 'react'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter} from '@github-ui/filter'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner-ui/contextual'
import {SidebarPageLayout} from '@github-ui/layout-primitives/SidebarPageLayout'
import {PreviewCardOutlet} from '@github-ui/preview-card'
import {MultiSelectReposPicker} from '@github-ui/repos-picker'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Outlet, useLocation} from '@github-ui/react-router'
import {DashboardSidebarCollapsible} from '@github-ui/shared-dashboard-sidebar/DashboardSidebarCollapsible'
import {Banner, Link} from '@primer/react'
import {pullsDashboardSurfaceLayoutRoute} from './pulls-dashboard-surface-layout-route'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {
  PullsDashboardSurfaceStateProvider,
  usePullsDashboardActions,
  usePullsDashboardFetchedQuery,
  usePullsDashboardIsLoading,
  usePullsDashboardMaxPrAge,
  usePullsDashboardPayload,
} from '../contexts/PullsDashboardSurfaceStateContext'
import {usePullsDashboardSurfaceLayoutState} from '../hooks/use-pulls-dashboard-surface-layout-state'
import {ShortcutEditingHeader} from '@github-ui/saved-views'
import {InboxPrAgeFilter} from '../components/lists/pulls/InboxPrAgeFilter'
import {isOnInboxPath} from '@github-ui/shared-dashboard-surface/helpers/is-on-inbox'
import {CustomizeSectionsDialog} from '../components/customize-sections/CustomizeSectionsDialog'
import {getAvailableInboxSections, useDefaultInboxSectionOrder} from '../constants/inbox-sections'

import type {PullsDashboardLayoutPayload} from '../types/routes-types'
import {getAvailableInboxSectionIds} from './pulls-dashboard-inbox-route'
import {pullsDashboardSurfaceContentRoute} from './pulls-dashboard-surface-content-route'

export function PullsDashboardSurfaceLayoutComponent() {
  const {data: payload, isPending: isLoading} = useRouteQuery(pullsDashboardSurfaceLayoutRoute, 'mainQuery')

  return <PullsDashboardSurfaceLayoutRoot payload={payload} isLoading={isLoading} />
}

export function PullsDashboardSurfaceLayoutRoot({
  payload,
  isLoading,
}: {
  payload: PullsDashboardLayoutPayload | undefined
  isLoading: boolean
}) {
  const isDegradationBannerEnabled = isFeatureEnabled('pulls_new_es_degraded_banner')

  return (
    <AnalyticsProvider
      appName="pulls-dashboard-surface"
      category="Dashboard Surface"
      metadata={{page: 'pulls-dashboard'}}
    >
      <ContextualGlobalSsoBanner />
      {isDegradationBannerEnabled ? (
        <Banner data-testid="es-degradation-banner" variant="warning" title="Warning" hideTitle flush>
          <Banner.Description>
            Some pull requests may be missing due to an{' '}
            <Link inline href="https://www.githubstatus.com/incidents/x69zbgdyfzg0">
              ongoing search incident
            </Link>
            , but no data is lost. Use the{' '}
            <Link inline href="https://docs.github.com/en/rest/pulls/pulls?apiVersion=2026-03-10#list-pull-requests">
              API
            </Link>{' '}
            or{' '}
            <Link inline href="https://docs.github.com/en/github-cli">
              GitHub CLI
            </Link>{' '}
            (<code>gh pr list</code>) for complete pull request results.
          </Banner.Description>
        </Banner>
      ) : null}
      <div data-testid="pulls-dashboard-surface-layout" data-issue-and-pr-hovercards-enabled>
        {payload && (
          <PullsDashboardSurfaceStateProvider payload={payload} isLoading={isLoading}>
            <PullsDashboardSurfaceLayout />
          </PullsDashboardSurfaceStateProvider>
        )}
        <PreviewCardOutlet />
      </div>
    </AnalyticsProvider>
  )
}

function PullsDashboardSurfaceLayout() {
  const {
    sidebarLinks,
    savedViews,
    selectedSidebarLink,
    editing,
    deleting,
    methods,
    pullsFilterProps,
    inboxPickerProps,
  } = usePullsDashboardSurfaceLayoutState()

  const payload = usePullsDashboardPayload()
  const fetchedQuery = usePullsDashboardFetchedQuery()
  const isLoading = usePullsDashboardIsLoading()
  const maxPrAge = usePullsDashboardMaxPrAge()
  const {setSectionPreferences} = usePullsDashboardActions()
  const {pathname} = useLocation()
  const isOnInbox = isOnInboxPath({pathname})

  const savedViewsEnabled = true

  const [customizeOpen, setCustomizeOpen] = useState(false)
  const customizeSectionsButtonRef = useRef<HTMLButtonElement>(null)

  // Derive dialog availability from the frontend section registry rather than
  // `inboxSectionQueries`, the dialog still needs to show hidden sections so they can be
  // restored.
  const availableSectionIds = useMemo(() => getAvailableInboxSectionIds(), [])
  const availableSections = useMemo(() => getAvailableInboxSections(availableSectionIds), [availableSectionIds])
  const defaultOrder = useDefaultInboxSectionOrder(availableSectionIds)

  const visibleLinks = useMemo(() => sidebarLinks, [sidebarLinks])
  const generateLinkPath = (path: string | undefined, _query?: string) => {
    return pullsDashboardSurfaceContentRoute.generatePath({mode: path || ''})
  }

  const header =
    editing.shortcut && !isOnInbox ? (
      <ShortcutEditingHeader
        renderFilter={filterProps => (
          <Filter {...filterProps} {...pullsFilterProps} data-testid="pulls-filter-component" />
        )}
        onSave={editing.saveAndDismiss}
        onDismiss={editing.dismiss}
        shortcut={editing.shortcut}
        setShortcutValue={editing.setValue}
        isSaving={editing.isSaving}
        errorMessage={editing.errorMessage}
      />
    ) : (
      <SharedHeader
        dashboardType="pull-requests"
        links={visibleLinks}
        selectedLink={selectedSidebarLink}
        defaultViewId={payload.defaultViewId}
        inboxOptionsTipDismissed={payload.inboxOptionsTipDismissed}
        optOutLocked
        onSelectLink={methods.selectSidebarLink}
        rightSideContent={
          isOnInbox && inboxPickerProps ? (
            <div className="d-flex flex-wrap gap-2" data-testid="pulls-inbox-picker-filter">
              <MultiSelectReposPicker
                scope={inboxPickerProps.scope}
                selected={inboxPickerProps.selectedRepos}
                onSubmit={inboxPickerProps.onSubmit}
              />
              <InboxPrAgeFilter selectedMaxPrAge={maxPrAge} onMaxPrAgeChange={inboxPickerProps.handleMaxPrAgeChange} />
            </div>
          ) : undefined
        }
        filter={!isOnInbox ? <Filter {...pullsFilterProps} data-testid="pulls-filter-component" /> : undefined}
        savedViews={savedViews}
        showSavedViews={savedViewsEnabled}
        onCreateView={methods.createShortcut}
        onEditView={methods.editShortcut}
        onDeleteView={deleting.deleteShortcut}
        onUpdateView={methods.updateShortcut}
        isViewModified={!isLoading && fetchedQuery !== selectedSidebarLink?.query}
        deleteErrorMessage={deleting.errorMessage}
        generateLinkPath={generateLinkPath}
        onCustomizeSections={isOnInbox ? () => setCustomizeOpen(true) : undefined}
        customizeSectionsButtonRef={customizeSectionsButtonRef}
        showInboxOptionsTip={isOnInbox}
      />
    )

  const sidebarProps = {
    dashboardType: 'pull-requests',
    links: visibleLinks,
    selectedLink: selectedSidebarLink?.id || '',
    onSelectLink: methods.selectSidebarLink,
    savedViews,
    showSavedViews: savedViewsEnabled,
    onCreateView: methods.createShortcut,
    generateLinkPath,
  } satisfies ComponentProps<typeof DashboardSidebarCollapsible>

  return (
    <>
      <SidebarPageLayout storageKey="pulls-dashboard-sidebar" initialExpanded={payload.sidebarExpanded}>
        <SidebarPageLayout.Header>{header}</SidebarPageLayout.Header>
        <SidebarPageLayout.Sidebar aria-label="Pull requests navigation" dialogTitle="Pull requests">
          <DashboardSidebarCollapsible {...sidebarProps} />
        </SidebarPageLayout.Sidebar>
        <SidebarPageLayout.Content as="div">
          <Outlet />
        </SidebarPageLayout.Content>
      </SidebarPageLayout>
      {isOnInbox && customizeOpen && (
        <CustomizeSectionsDialog
          open={customizeOpen}
          onOpenChange={setCustomizeOpen}
          initialOrder={payload.sectionOrder ?? []}
          initialHidden={payload.hiddenSections ?? []}
          availableSections={availableSections}
          defaultOrder={defaultOrder}
          onSaved={setSectionPreferences}
          returnFocusRef={customizeSectionsButtonRef}
        />
      )}
    </>
  )
}
